import type { Recado } from '@domain/recado'
import { pedacosDe } from './links'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

/**
 * O recado aberto, por inteiro.
 *
 * ## Por que ele não cabe na lista
 *
 * O primeiro recado escrito de verdade tinha **932 caracteres** — um achado com
 * medições, causa e ressalva. Inteiro na lista, ele empurrava sete notificações
 * para fora da tela e transformava uma lista de linhas curtas num paredão.
 * Truncado sem lugar para abrir, ele seria pior: metade de uma frase é pior que
 * nenhuma.
 *
 * ## E por que ele não é uma tela
 *
 * Uma rota própria daria endereço a um recado — e daí "voltar", histórico,
 * link compartilhável para um bilhete de casa. A folha abre e fecha sobre a
 * lista, que é a profundidade certa: ler e voltar, sem sair do lugar.
 *
 * ## O link vira LINK, e o endereço cru some
 *
 * Um recado real trouxe quatro links de Google Maps com mais de 200 caracteres
 * de `%20` cada. Escritos por extenso eles afogavam a informação — loja, preço,
 * distância — no meio do próprio escape de URL.
 *
 * `rel="noopener noreferrer"` e `target="_blank"`: o texto vem de fora, e uma
 * aba aberta a partir daqui não precisa de referência a esta. E só `http`/
 * `https` chegam a virar `<a>` — ver `links.ts`, que é onde essa recusa mora.
 *
 * ## Apagar mora AQUI, e não na linha
 *
 * Na linha ele ficava a um toque de distância do gesto de ler, numa lista onde
 * o dedo passa rolando. Aqui é preciso abrir primeiro — e quem abriu leu o que
 * está apagando.
 */
export function RecadoPorInteiro({
  recado,
  quando,
  onApagar,
  onFechar,
}: {
  recado: Recado
  /** A data já formatada — quem formata é a tela, que sabe o ano corrente. */
  quando: string
  onApagar: () => void
  onFechar: () => void
}) {
  return (
    <FolhaDeBaixo titulo={`Recado de ${recado.autor.label}`} onFechar={onFechar}>
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="titulo text-base" style={{ color: 'var(--economia)' }}>
          {recado.autor.label}
        </h2>
        <span className="text-xs text-[var(--text-muted)]">{quando}</span>
      </div>

      {/* `whitespace-pre-wrap` e `break-words`: o texto vem com quebras de
          linha, e sem o segundo um endereço sem espaços estoura a largura. */}
      <p className="max-h-[60vh] overflow-y-auto text-sm break-words whitespace-pre-wrap">
        {pedacosDe(recado.texto).map((pedaco, i) =>
          pedaco.tipo === 'link' ? (
            <a
              // O índice como `key`: os pedaços vêm de um texto imutável, a
              // lista não reordena nem cresce, e o recado não é editável.
              key={i}
              href={pedaco.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: 'var(--economia)' }}
              // O endereço inteiro fica no `title` para quem tem mouse: o
              // rótulo diz para ONDE vai, e isto diz exatamente para onde.
              title={pedaco.href}
            >
              {pedaco.rotulo}
            </a>
          ) : (
            <span key={i}>{pedaco.texto}</span>
          ),
        )}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <Botao type="button" onClick={onFechar}>
          Fechar
        </Botao>
        <Botao
          type="button"
          tom="perigo"
          onClick={() => {
            onApagar()
            onFechar()
          }}
        >
          Apagar
        </Botao>
      </div>
    </FolhaDeBaixo>
  )
}
